import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { apiGet, DEFAULT_AVATAR, type Patient } from "../api/client";

export const PRIMARY_NAV = [
  { to: "/app/activities", label: "Activities" },
  { to: "/app/messages", label: "Messages" },
  { to: "/app/patients", label: "Patients" },
  { to: "/app/calendar", label: "Calendar" },
  { to: "/app/team", label: "Team" },
  { to: "/app/communications", label: "Communications" },
  { to: "/app/analytics", label: "Analytics" },
] as const;

export type NavBarProps = {
  /** Prefill notification badge (Storybook / tests). */
  messageCount?: number;
  /** Prefill search results (Storybook). */
  searchResults?: Patient[];
  /** When false, skip live API (stories). */
  live?: boolean;
  activePath?: string;
};

/**
 * Historical top nav (`ne__nav`) with brand, primary links, search, and badge.
 */
export function NavBar({
  messageCount: messageCountProp,
  searchResults,
  live = true,
  activePath,
}: NavBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<Patient[]>(searchResults ?? []);
  const [drawer, setDrawer] = useState<"messages" | null>(null);
  const [messageNotifs, setMessageNotifs] = useState<
    Array<{ owner_name?: string; parameters?: { data?: string }; date?: string }>
  >([]);

  useEffect(() => {
    if (!live) return;
    let cancelled = false;
    apiGet<{
      activities?: {
        messages?: Array<{
          owner_name?: string;
          parameters?: { data?: string };
          date?: string;
        }>;
      };
    }>("/notifications")
      .then((data) => {
        if (cancelled) return;
        const bundle = Array.isArray(data) ? data[0] : data;
        setMessageNotifs(bundle?.activities?.messages ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [live]);

  useEffect(() => {
    if (searchResults) {
      setHits(searchResults);
      return;
    }
    if (!live) return;
    if (query.trim().length < 3) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      apiGet<Patient[]>("/patient_searches", { name: query.trim() })
        .then((data) => {
          if (!cancelled) setHits(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          if (!cancelled) setHits([]);
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query, live, searchResults]);

  const messageCount = useMemo(() => {
    if (messageCountProp != null) return messageCountProp;
    return messageNotifs.length;
  }, [messageCountProp, messageNotifs.length]);

  function onSearchSubmit(event: FormEvent) {
    event.preventDefault();
    if (hits[0]) {
      navigate(`/app/patients/${hits[0].id}/activity`);
      setSearchOpen(false);
      setQuery("");
      setHits([]);
    }
  }

  return (
    <>
      <nav className="ne__nav" role="navigation">
        <Link className="ne__nav__brand" to="/app/activities">
          <div className="ne__logo">
            <img
              src="/app/img/logo/nexhealth-logo--invert.svg"
              alt="NexHealth logo"
            />
          </div>
        </Link>
        <div className="ne__nav__wrapper">
          <ul className="ne__nav__primary">
            <div className="ne__nav__primary__group">
              {PRIMARY_NAV.map((item) => {
                const active =
                  activePath === item.to ||
                  location.pathname === item.to ||
                  location.pathname.startsWith(`${item.to}/`);
                return (
                  <li key={item.to} className={active ? "active" : undefined}>
                    <NavLink
                      to={item.to}
                      className={`ne__nav__item ne__nav__item--primary${
                        active ? " active" : ""
                      }`}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </div>
          </ul>
          <ul className="ne__nav__secondary" id="ne__nav__secondary">
            {/* Historical secondary strip: location · messages · check-ins · appt requests · search · avatar */}
            <li className="ne__dropdown ne__dropdown--secondary">
              <a href="#locations" onClick={(e) => e.preventDefault()} aria-label="Locations">
                <i className="ne__icon__location-simple--thin" />
              </a>
            </li>
            <li className="ne__dropdown ne__dropdown--secondary">
              <a
                href="#messages"
                onClick={(e) => {
                  e.preventDefault();
                  setDrawer(drawer === "messages" ? null : "messages");
                }}
                aria-label="Messages"
              >
                <i className="ne__icon__messages--thin" />
                {messageCount > 0 ? (
                  <span className="ne__badge ne__badge--num ne__badge--primary badge">
                    {messageCount}
                  </span>
                ) : null}
              </a>
            </li>
            <li className="ne__dropdown ne__dropdown--secondary">
              <a href="#checkins" onClick={(e) => e.preventDefault()} aria-label="Today's appointments">
                <i className="ne__icon__calendar--thin" />
                <span className="ne__badge ne__badge--num ne__badge--primary badge">1</span>
              </a>
            </li>
            <li className="ne__dropdown ne__dropdown--secondary">
              <a href="#apptreqs" onClick={(e) => e.preventDefault()} aria-label="Appointment requests">
                <i className="ne__icon__bell--thin" />
                <span className="ne__badge ne__badge--num ne__badge--primary badge">5</span>
              </a>
            </li>
            <li>
              <a
                className="ne__nav-search__toggle"
                href="#search"
                onClick={(e) => {
                  e.preventDefault();
                  setSearchOpen((v) => !v);
                }}
                aria-label="Search patients"
              >
                <i className="ne__icon__search--thin" />
              </a>
            </li>
            <li>
              <NavLink to="/app/settings/personal">
                <figure className="ne__avatar ne__avatar--nav">
                  <img src={DEFAULT_AVATAR} alt="Alex Rivera" />
                </figure>
              </NavLink>
            </li>
          </ul>
        </div>
        <form
          className={`navbar-form usersearch ne__nav-search${searchOpen ? " open" : ""}`}
          role="search"
          onSubmit={onSearchSubmit}
        >
          <div className="form-group has-feedback ne__typeahead">
            <input
              type="text"
              name="result"
              placeholder="Type patient name"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onBlur={() => {
                window.setTimeout(() => {
                  if (!searchResults) setHits([]);
                }, 150);
              }}
            />
            {hits.length > 0 ? (
              <ul className="dropdown-menu" style={{ display: "block" }}>
                {hits.map((patient) => (
                  <li key={patient.id}>
                    <a
                      className="ne__search-result ne__search-result--patient"
                      href={`/app/patients/${patient.id}/activity`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/app/patients/${patient.id}/activity`);
                        setSearchOpen(false);
                        setQuery("");
                        if (!searchResults) setHits([]);
                      }}
                    >
                      <div className="ne__avatar">
                        <img
                          src={patient.profile_url || DEFAULT_AVATAR}
                          alt=""
                        />
                      </div>
                      <div className="ne__search-result__info">
                        <h5 className="ne__search-result__name">
                          {patient.name}
                        </h5>
                        <small className="ne__search-result__dob">
                          {patient.bio?.date_of_birth}
                        </small>
                        <span className="ne__search-result__email">
                          {patient.email}
                        </span>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <button className="ne__nav-search__submit" type="submit">
            Submit
          </button>
        </form>
      </nav>

      {drawer === "messages" ? (
        <aside className="ne__nav-drawer" style={{ padding: "1rem" }}>
          <h4>Messages</h4>
          <ul className="list-group">
            {messageNotifs.map((n, i) => (
              <li key={i} className="list-group-item">
                <strong>{n.owner_name}</strong>
                <div>{n.parameters?.data}</div>
              </li>
            ))}
            {!messageNotifs.length ? (
              <li className="list-group-item">No new messages</li>
            ) : null}
          </ul>
        </aside>
      ) : null}
    </>
  );
}
